import type {
  DetectedRecoveryInterval,
  DetectedWorkInterval,
  IntervalClassification,
  IntervalDetectionResult,
  WorkoutClassificationResult,
  WorkoutDetectedSet,
  WorkoutHeartRateProgression,
  WorkoutPowerFadeStatus,
  WorkoutType
} from "../types/ride";

type WorkoutClassificationAnalyticsInput = {
  averagePower: number | null;
  normalizedPower: number | null;
  intensityFactor: number | null;
  variabilityIndex?: number | null;
  workKilojoules: number | null;
  validPowerDurationSeconds: number | null;
  powerZones?: Array<{ zoneName: string; seconds: number | null; percentageOfMovingTime: number | null }>;
};

type WorkoutPattern = {
  workoutType: WorkoutType;
  workoutName: string;
  targetRepetitions: number;
  targetDurationSeconds: number;
};

type StructuredSetEvaluation = {
  setId: string;
  workoutType: WorkoutType;
  classification: IntervalClassification;
  intervals: DetectedWorkInterval[];
  recoveries: DetectedRecoveryInterval[];
  averageDurationSeconds: number | null;
  averagePower: number | null;
  averagePercentOfFTP: number | null;
  averageRecoverySeconds: number | null;
  powerVariationPercent: number | null;
  durationVariationPercent: number | null;
  cadenceVariationPercent: number | null;
  heartRateProgression: WorkoutHeartRateProgression;
  confidence: number;
  coherence: number;
  totalWorkSeconds: number;
};

export type WorkoutClassificationConfiguration = {
  durationTolerancePercent: number;
  repetitionTolerance: number;
  recoveryIntensityIfUpperBound: number;
  enduranceIfUpperBound: number;
  tempoIfUpperBound: number;
  stablePowerFadePercent: number;
  stableHeartRateDeltaBpm: number;
  minStructuredSetDurationSeconds: number;
  structuredSecondaryContributionThreshold: number;
};

const DEFAULT_CONFIGURATION: WorkoutClassificationConfiguration = {
  durationTolerancePercent: 0.12,
  repetitionTolerance: 1,
  recoveryIntensityIfUpperBound: 0.55,
  enduranceIfUpperBound: 0.76,
  tempoIfUpperBound: 0.91,
  stablePowerFadePercent: 2.0,
  stableHeartRateDeltaBpm: 3,
  minStructuredSetDurationSeconds: 120,
  structuredSecondaryContributionThreshold: 0.2
};

const STRUCTURED_WORKOUT_TYPES: WorkoutType[] = ["threshold", "sweet-spot", "vo2max", "anaerobic", "sprint", "tempo"];

const CLASSIFICATION_TO_WORKOUT_TYPE: Record<IntervalClassification, WorkoutType> = {
  sprint: "sprint",
  anaerobic: "anaerobic",
  vo2max: "vo2max",
  threshold: "threshold",
  "sweet-spot": "sweet-spot",
  tempo: "tempo",
  endurance: "endurance",
  unclassified: "unclassified"
};

const WORKOUT_PATTERNS: WorkoutPattern[] = [
  { workoutType: "threshold", workoutName: "2 x 8 min Threshold", targetRepetitions: 2, targetDurationSeconds: 8 * 60 },
  { workoutType: "threshold", workoutName: "3 x 8 min Threshold", targetRepetitions: 3, targetDurationSeconds: 8 * 60 },
  { workoutType: "threshold", workoutName: "3 x 10 min Threshold", targetRepetitions: 3, targetDurationSeconds: 10 * 60 },
  { workoutType: "threshold", workoutName: "4 x 8 min Threshold", targetRepetitions: 4, targetDurationSeconds: 8 * 60 },
  { workoutType: "threshold", workoutName: "2 x 15 min Threshold", targetRepetitions: 2, targetDurationSeconds: 15 * 60 },
  { workoutType: "threshold", workoutName: "2 x 20 min Threshold", targetRepetitions: 2, targetDurationSeconds: 20 * 60 },
  { workoutType: "sweet-spot", workoutName: "3 x 12 min Sweet Spot", targetRepetitions: 3, targetDurationSeconds: 12 * 60 },
  { workoutType: "threshold", workoutName: "3 x 12 min Threshold", targetRepetitions: 3, targetDurationSeconds: 12 * 60 },
  { workoutType: "vo2max", workoutName: "4 x 4 min VO2 Max", targetRepetitions: 4, targetDurationSeconds: 4 * 60 },
  { workoutType: "vo2max", workoutName: "5 x 5 min VO2 Max", targetRepetitions: 5, targetDurationSeconds: 5 * 60 },
  { workoutType: "vo2max", workoutName: "6 x 3 min VO2 Max", targetRepetitions: 6, targetDurationSeconds: 3 * 60 }
];

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function average(values: Array<number | null>): number | null {
  const valid = values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  if (valid.length === 0) {
    return null;
  }

  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
}

function standardDeviation(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }

  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

function variationPercent(values: number[]): number | null {
  if (values.length < 2) {
    return null;
  }

  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  if (mean <= 0) {
    return null;
  }

  return (standardDeviation(values) / mean) * 100;
}

function consistencyPercent(values: number[]): number | null {
  const variation = variationPercent(values);
  if (variation === null) {
    return null;
  }

  return clamp(100 - variation, 0, 100);
}

function deriveHeartRateProgression(heartRates: number[], stableDeltaBpm: number): WorkoutHeartRateProgression {
  if (heartRates.length < 2) {
    return "unavailable";
  }

  const first = heartRates[0];
  const last = heartRates[heartRates.length - 1];
  const delta = last - first;

  if (delta >= stableDeltaBpm) {
    return "rising";
  }

  if (delta <= -stableDeltaBpm) {
    return "falling";
  }

  const spread = Math.max(...heartRates) - Math.min(...heartRates);
  if (spread <= stableDeltaBpm) {
    return "stable";
  }

  return "variable";
}

function derivePowerFadeStatus(powerFadePercent: number | null, stablePowerFadePercent: number): WorkoutPowerFadeStatus {
  if (powerFadePercent === null || !Number.isFinite(powerFadePercent)) {
    return "unavailable";
  }

  if (powerFadePercent > stablePowerFadePercent) {
    return "fading";
  }

  if (powerFadePercent < -stablePowerFadePercent) {
    return "improving";
  }

  return "stable";
}

function toWorkoutType(classification: IntervalClassification): WorkoutType {
  return CLASSIFICATION_TO_WORKOUT_TYPE[classification];
}

function displayWorkoutType(type: WorkoutType): string {
  switch (type) {
    case "vo2max":
      return "VO2 Max";
    case "sweet-spot":
      return "Sweet Spot";
    case "anaerobic":
      return "Anaerobic";
    case "threshold":
      return "Threshold";
    case "tempo":
      return "Tempo";
    case "endurance":
      return "Endurance";
    case "sprint":
      return "Sprints";
    case "recovery":
      return "Recovery";
    case "mixed":
      return "Mixed";
    case "unclassified":
    default:
      return "Unclassified";
  }
}

function evaluateStructuredSet(
  setId: string,
  intervals: DetectedWorkInterval[],
  recoveries: DetectedRecoveryInterval[],
  configuration: WorkoutClassificationConfiguration
): StructuredSetEvaluation {
  const workoutType = toWorkoutType(intervals[0]?.classification ?? "unclassified");
  const powers = intervals.map(interval => interval.averagePower).filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const durations = intervals.map(interval => interval.durationSeconds);
  const percents = intervals.map(interval => interval.percentOfFTP).filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const cadences = intervals.map(interval => interval.averageCadence).filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const heartRates = intervals.map(interval => interval.averageHeartRate).filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const confidences = intervals.map(interval => interval.confidence);

  const averagePower = average(powers);
  const averageDurationSeconds = average(durations);
  const averagePercentOfFTP = average(percents);
  const averageRecoverySeconds = average(recoveries.map(item => item.durationSeconds));

  const powerVariationPercent = variationPercent(powers);
  const durationVariationPercent = variationPercent(durations);
  const cadenceVariationPercent = variationPercent(cadences);

  const averageConfidence = average(confidences) ?? 0;
  const coherenceFromPower = 1 - clamp((powerVariationPercent ?? 20) / 30, 0, 1);
  const coherenceFromDuration = 1 - clamp((durationVariationPercent ?? 20) / 30, 0, 1);
  const coherence = clamp(0.45 * coherenceFromPower + 0.35 * coherenceFromDuration + 0.2 * averageConfidence, 0, 1);

  const repetitionScore = clamp((intervals.length - 1) / 4, 0, 1);
  const contributionScore = clamp((intervals.reduce((sum, interval) => sum + interval.durationSeconds, 0) - configuration.minStructuredSetDurationSeconds) / 1800, 0, 1);
  const confidence = clamp(0.4 * coherence + 0.35 * repetitionScore + 0.25 * contributionScore, 0, 1);

  return {
    setId,
    workoutType,
    classification: intervals[0]?.classification ?? "unclassified",
    intervals,
    recoveries,
    averageDurationSeconds,
    averagePower,
    averagePercentOfFTP,
    averageRecoverySeconds,
    powerVariationPercent,
    durationVariationPercent,
    cadenceVariationPercent,
    heartRateProgression: deriveHeartRateProgression(heartRates, configuration.stableHeartRateDeltaBpm),
    confidence,
    coherence,
    totalWorkSeconds: intervals.reduce((sum, interval) => sum + interval.durationSeconds, 0)
  };
}

function buildStructuredSets(
  intervalDetectionResult: IntervalDetectionResult,
  configuration: WorkoutClassificationConfiguration
): StructuredSetEvaluation[] {
  const recoveriesByIntervalId = new Map(intervalDetectionResult.detectedRecoveries.map(item => [item.associatedIntervalId, item]));

  const grouped = new Map<string, DetectedWorkInterval[]>();
  for (const interval of intervalDetectionResult.detectedIntervals) {
    const setId = interval.setId ?? `single-${interval.classification}-${interval.id}`;
    const existing = grouped.get(setId) ?? [];
    existing.push(interval);
    grouped.set(setId, existing);
  }

  const evaluations: StructuredSetEvaluation[] = [];
  for (const [setId, intervals] of grouped.entries()) {
    const ordered = [...intervals].sort((left, right) => left.startElapsedSeconds - right.startElapsedSeconds);
    const recoveries = ordered
      .map(interval => recoveriesByIntervalId.get(interval.id))
      .filter((item): item is DetectedRecoveryInterval => !!item);

    const classificationType = toWorkoutType(ordered[0]?.classification ?? "unclassified");
    const isStructured = STRUCTURED_WORKOUT_TYPES.includes(classificationType);
    if (!isStructured) {
      continue;
    }

    evaluations.push(evaluateStructuredSet(setId, ordered, recoveries, configuration));
  }

  return evaluations.sort((left, right) => {
    const leftScore = left.totalWorkSeconds * (0.7 + left.coherence * 0.3) * (0.6 + left.confidence * 0.4);
    const rightScore = right.totalWorkSeconds * (0.7 + right.coherence * 0.3) * (0.6 + right.confidence * 0.4);
    return rightScore - leftScore;
  });
}

function selectPrimaryStructuredSet(sets: StructuredSetEvaluation[]): StructuredSetEvaluation | null {
  if (sets.length === 0) {
    return null;
  }

  return sets[0];
}

function buildMixedWorkoutName(primary: WorkoutType, secondary: WorkoutType[]): string {
  const components = [primary, ...secondary].map(displayWorkoutType);
  return `Mixed Workout: ${components.join(" + ")}`;
}

function shouldIncludeSecondarySet(
  set: StructuredSetEvaluation,
  totalStructuredWork: number,
  configuration: WorkoutClassificationConfiguration
): boolean {
  const contributionRatio = set.totalWorkSeconds / Math.max(1, totalStructuredWork);
  if (contributionRatio >= configuration.structuredSecondaryContributionThreshold) {
    return true;
  }

  // Keep short, high-intensity components (e.g., sprints/anaerobic add-ons) visible in mixed labeling.
  if (set.intervals.length >= 3 && set.confidence >= 0.5) {
    return true;
  }

  if (set.workoutType === "sprint" && set.intervals.length >= 4) {
    return true;
  }

  return false;
}

function calculatePowerTrendPercentPerRep(intervals: DetectedWorkInterval[]): number | null {
  const points = intervals
    .map((interval, index) => ({ x: index, y: interval.averagePower }))
    .filter((item): item is { x: number; y: number } => typeof item.y === "number" && Number.isFinite(item.y));

  if (points.length < 2) {
    return null;
  }

  const meanX = points.reduce((sum, point) => sum + point.x, 0) / points.length;
  const meanY = points.reduce((sum, point) => sum + point.y, 0) / points.length;
  const denominator = points.reduce((sum, point) => sum + (point.x - meanX) ** 2, 0);
  if (denominator <= 0 || meanY <= 0) {
    return null;
  }

  const slope = points.reduce((sum, point) => sum + (point.x - meanX) * (point.y - meanY), 0) / denominator;
  return (slope / meanY) * 100;
}

function matchPattern(primarySet: StructuredSetEvaluation, configuration: WorkoutClassificationConfiguration): { name: string; plannedSeconds: number | null } {
  const candidatePatterns = WORKOUT_PATTERNS.filter(pattern => pattern.workoutType === primarySet.workoutType);
  if (primarySet.averageDurationSeconds === null) {
    return { name: `${displayWorkoutType(primarySet.workoutType)} Workout`, plannedSeconds: null };
  }

  let best: { pattern: WorkoutPattern; score: number } | null = null;
  for (const pattern of candidatePatterns) {
    const repError = Math.abs(primarySet.intervals.length - pattern.targetRepetitions);
    if (repError > configuration.repetitionTolerance) {
      continue;
    }

    const durationError = Math.abs(primarySet.averageDurationSeconds - pattern.targetDurationSeconds) / pattern.targetDurationSeconds;
    if (durationError > configuration.durationTolerancePercent * 2) {
      continue;
    }

    const repScore = clamp(1 - repError / Math.max(1, pattern.targetRepetitions), 0, 1);
    const durationScore = clamp(1 - durationError, 0, 1);
    const score = 0.55 * repScore + 0.45 * durationScore;

    if (!best || score > best.score) {
      best = { pattern, score };
    }
  }

  if (best && best.score >= 0.62) {
    return {
      name: best.pattern.workoutName,
      plannedSeconds: best.pattern.targetRepetitions * best.pattern.targetDurationSeconds
    };
  }

  if (primarySet.workoutType === "tempo") {
    return { name: "Sustained Tempo", plannedSeconds: null };
  }

  return {
    name: `${primarySet.intervals.length} x ${Math.round((primarySet.averageDurationSeconds ?? 0) / 60)} min ${displayWorkoutType(primarySet.workoutType)}`,
    plannedSeconds: null
  };
}

function emptyResult(status: WorkoutClassificationResult["status"], warnings: string[] = []): WorkoutClassificationResult {
  return {
    status,
    workoutType: "unclassified",
    workoutName: "Unknown Ride",
    summary: "Insufficient workout data for classification.",
    confidence: 0,
    classificationCategory: "free-ride",
    explanation: "Insufficient ride evidence for a confident workout classification.",
    confidenceFactors: [],
    continuousEffortSummary: "--",
    primaryStructuredType: null,
    secondaryStructuredTypes: [],
    structuredSetCount: 0,
    primaryIntervalClassification: null,
    repetitionCount: null,
    plannedEquivalentDurationSeconds: null,
    averageWorkIntervalDurationSeconds: null,
    averageRecoveryDurationSeconds: null,
    averageWorkPower: null,
    averageWorkPercentOfFTP: null,
    averagePowerAcrossPrimarySet: null,
    powerVariationPercent: null,
    durationVariationPercent: null,
    intervalConsistencyPercent: null,
    firstRepAveragePower: null,
    finalRepAveragePower: null,
    powerFadePercent: null,
    powerTrendPercentPerRep: null,
    powerFadeStatus: "unavailable",
    averageCadenceAcrossPrimarySet: null,
    cadenceVariationPercent: null,
    cadenceConsistencyPercent: null,
    firstRepAverageHeartRate: null,
    finalRepAverageHeartRate: null,
    heartRateChangeBpm: null,
    heartRateChangePercent: null,
    heartRateProgression: "unavailable",
    completedWorkDurationSeconds: null,
    supportingSegmentSummary: "--",
    detectedSets: [],
    warnings
  };
}

function classifyNoIntervals(
  intervalDetectionResult: IntervalDetectionResult,
  analytics: WorkoutClassificationAnalyticsInput,
  ftpInput: number | null | undefined,
  configuration: WorkoutClassificationConfiguration
): WorkoutClassificationResult {
  if (!ftpInput || ftpInput <= 0) {
    return emptyResult("invalid-ftp", [...intervalDetectionResult.warnings]);
  }

  const intensityFactor = analytics.intensityFactor;
  if (intensityFactor === null || !Number.isFinite(intensityFactor)) {
    return emptyResult("insufficient-data", [...intervalDetectionResult.warnings]);
  }

  const continuousSegments = intervalDetectionResult.continuousEffortSegments ?? [];
  const zonePercent = (zoneName: string) => analytics.powerZones?.find(zone => zone.zoneName === zoneName)?.percentageOfMovingTime ?? null;
  const z1 = zonePercent("Zone 1") ?? 0;
  const z2 = zonePercent("Zone 2") ?? 0;
  const z3 = zonePercent("Zone 3") ?? 0;
  const z4plus = ["Zone 4", "Zone 5", "Zone 6", "Zone 7"].reduce((sum, zoneName) => sum + (zonePercent(zoneName) ?? 0), 0);
  const variabilityIndex = analytics.variabilityIndex;
  const durationHours = (analytics.validPowerDurationSeconds ?? 0) / 3600;

  let workoutType: WorkoutType = "unclassified";
  let workoutName = "Free Ride";
  let summary = "No repeated interval set was detected.";
  let explanation = "The ride did not contain repeated work and recovery patterns, so it was classified as a free ride.";

  if (intensityFactor <= configuration.recoveryIntensityIfUpperBound) {
    workoutType = "recovery";
    workoutName = "Recovery Ride";
    summary = "Low-intensity recovery profile without structured intervals.";
    explanation = `Intensity factor was ${intensityFactor.toFixed(2)} with low overall workload and no repeated interval structure.`;
  } else if (intensityFactor <= configuration.enduranceIfUpperBound) {
    workoutType = "endurance";
    workoutName = "Endurance Ride";
    summary = "Steady endurance ride without formal interval structure.";
    explanation = `Most ride time sat in aerobic zones with intensity factor ${intensityFactor.toFixed(2)} and no repeated interval structure.`;
  } else if (intensityFactor <= configuration.tempoIfUpperBound) {
    workoutType = "tempo";
    workoutName = "Sustained Tempo";
    summary = "Sustained tempo effort without repeated structured reps.";
    explanation = `The ride held sustained upper-aerobic to tempo power without repeated work-recovery repetition.`;
  } else {
    workoutType = "unclassified";
    workoutName = "Free Ride";
    summary = "High-intensity free ride without a coherent repeated interval set.";
    explanation = `Intensity factor was ${intensityFactor.toFixed(2)}, but the effort pattern did not form a repeated structured workout.`;
  }

  const confidenceFactors = [
    {
      label: "Power distribution",
      score: workoutType === "endurance" ? Math.min(1, (z1 + z2) / 75) : workoutType === "tempo" ? Math.min(1, z3 / 25) : workoutType === "recovery" ? Math.min(1, z1 / 70) : Math.min(1, z4plus / 20),
      reason: workoutType === "endurance" ? `Z1+Z2 accounted for ${Math.round(z1 + z2)}% of moving time.` : workoutType === "tempo" ? `Zone 3 accounted for ${Math.round(z3)}% of moving time.` : workoutType === "recovery" ? `Zone 1 accounted for ${Math.round(z1)}% of moving time.` : `Higher-intensity zones accounted for ${Math.round(z4plus)}% of moving time.`
    },
    {
      label: "Repetition score",
      score: 0,
      reason: "No repeated work-recovery set met the structured interval criteria."
    },
    {
      label: "Variability index",
      score: variabilityIndex === null || variabilityIndex === undefined ? 0.4 : workoutType === "endurance" ? Math.max(0, 1 - Math.abs(variabilityIndex - 1.05) / 0.12) : Math.max(0, 1 - Math.abs(variabilityIndex - 1.1) / 0.2),
      reason: variabilityIndex === null || variabilityIndex === undefined ? "Variability index was unavailable." : `Variability index was ${variabilityIndex.toFixed(2)}.`
    },
    {
      label: "Elapsed duration",
      score: workoutType === "recovery" ? Math.min(1, durationHours / 1.25) : Math.min(1, durationHours / 2),
      reason: `${durationHours.toFixed(1)} hours of valid ride time were available for classification.`
    }
  ];
  const confidence = clamp(confidenceFactors.reduce((sum, factor) => sum + factor.score, 0) / confidenceFactors.length, 0.35, 0.96);
  const continuousEffortSummary = continuousSegments.length > 0
    ? `Continuous effort segments: ${continuousSegments.map(segment => `${displayWorkoutType(toWorkoutType(segment.classification))} ${Math.round(segment.durationSeconds / 60)}m`).join(" • ")}`
    : "No continuous effort segments detected.";

  return {
    ...emptyResult("available", [...intervalDetectionResult.warnings]),
    workoutType,
    workoutName,
    summary,
    classificationCategory: "free-ride",
    confidence,
    explanation,
    confidenceFactors,
    repetitionCount: 0,
    completedWorkDurationSeconds: 0,
    supportingSegmentSummary: continuousEffortSummary,
    continuousEffortSummary
  };
}

export function classifyWorkout(
  intervalDetectionResult: IntervalDetectionResult,
  analytics: WorkoutClassificationAnalyticsInput,
  ftpInput: number | null | undefined,
  configurationInput?: Partial<WorkoutClassificationConfiguration>
): WorkoutClassificationResult {
  const configuration = { ...DEFAULT_CONFIGURATION, ...(configurationInput ?? {}) };

  if (!ftpInput || !Number.isFinite(ftpInput) || ftpInput <= 0) {
    return emptyResult("invalid-ftp", [...intervalDetectionResult.warnings, "FTP is invalid for workout classification."]);
  }

  const intervals = [...intervalDetectionResult.detectedIntervals].sort((left, right) => left.startElapsedSeconds - right.startElapsedSeconds);
  if (intervals.length === 0) {
    return classifyNoIntervals(intervalDetectionResult, analytics, ftpInput, configuration);
  }

  const structuredSets = buildStructuredSets(intervalDetectionResult, configuration);
  const primarySet = selectPrimaryStructuredSet(structuredSets);

  if (!primarySet) {
    return classifyNoIntervals(intervalDetectionResult, analytics, ftpInput, configuration);
  }

  const totalStructuredWork = structuredSets.reduce((sum, set) => sum + set.totalWorkSeconds, 0);
  const secondaryStructuredTypes = structuredSets
    .slice(1)
    .filter(set => shouldIncludeSecondarySet(set, totalStructuredWork, configuration))
    .map(set => set.workoutType)
    .filter((type, index, array) => array.indexOf(type) === index && type !== primarySet.workoutType);

  const isMixed = secondaryStructuredTypes.length > 0;
  const workoutType: WorkoutType = isMixed ? "mixed" : primarySet.workoutType;

  const patternMatch = matchPattern(primarySet, configuration);
  const workoutName = isMixed
    ? buildMixedWorkoutName(primarySet.workoutType, secondaryStructuredTypes)
    : patternMatch.name;

  const primaryIntervals = [...primarySet.intervals].sort((left, right) => left.startElapsedSeconds - right.startElapsedSeconds);
  const primaryPowers = primaryIntervals.map(interval => interval.averagePower).filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const primaryDurations = primaryIntervals.map(interval => interval.durationSeconds);
  const primaryCadence = primaryIntervals.map(interval => interval.averageCadence).filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const primaryHeartRates = primaryIntervals.map(interval => interval.averageHeartRate).filter((value): value is number => typeof value === "number" && Number.isFinite(value));

  const firstRepAveragePower = primaryIntervals[0]?.averagePower ?? null;
  const finalRepAveragePower = primaryIntervals[primaryIntervals.length - 1]?.averagePower ?? null;
  const powerFadePercent =
    typeof firstRepAveragePower === "number" && firstRepAveragePower > 0 && typeof finalRepAveragePower === "number"
      ? ((firstRepAveragePower - finalRepAveragePower) / firstRepAveragePower) * 100
      : null;

  const powerTrendPercentPerRep = calculatePowerTrendPercentPerRep(primaryIntervals);
  const powerFadeStatus = derivePowerFadeStatus(powerFadePercent, configuration.stablePowerFadePercent);

  const firstRepAverageHeartRate = primaryIntervals[0]?.averageHeartRate ?? null;
  const finalRepAverageHeartRate = primaryIntervals[primaryIntervals.length - 1]?.averageHeartRate ?? null;
  const heartRateChangeBpm =
    typeof firstRepAverageHeartRate === "number" && typeof finalRepAverageHeartRate === "number"
      ? finalRepAverageHeartRate - firstRepAverageHeartRate
      : null;
  const heartRateChangePercent =
    typeof heartRateChangeBpm === "number" && typeof firstRepAverageHeartRate === "number" && firstRepAverageHeartRate > 0
      ? (heartRateChangeBpm / firstRepAverageHeartRate) * 100
      : null;

  const averagePowerAcrossPrimarySet = average(primaryPowers);
  const averageDurationSeconds = average(primaryDurations);
  const powerVariationPercent = variationPercent(primaryPowers);
  const durationVariationPercent = variationPercent(primaryDurations);
  const cadenceVariationPercent = variationPercent(primaryCadence);

  const intervalConsistencyPercent = consistencyPercent(primaryPowers);
  const cadenceConsistencyPercent = consistencyPercent(primaryCadence);
  const heartRateProgression = deriveHeartRateProgression(primaryHeartRates, configuration.stableHeartRateDeltaBpm);

  const supportingIntervals = intervals.filter(interval => !primaryIntervals.some(primary => primary.id === interval.id));
  const supportingDuration = supportingIntervals.reduce((sum, interval) => sum + interval.durationSeconds, 0);
  const supportingClassificationMap = new Map<WorkoutType, number>();
  for (const interval of supportingIntervals) {
    const type = toWorkoutType(interval.classification);
    supportingClassificationMap.set(type, (supportingClassificationMap.get(type) ?? 0) + interval.durationSeconds);
  }
  const supportingParts = [...supportingClassificationMap.entries()]
    .sort((left, right) => right[1] - left[1])
    .map(([type, seconds]) => `${displayWorkoutType(type)} ${Math.round(seconds / 60)}m`);

  const supportingSegmentSummary =
    supportingDuration > 0
      ? `Supporting segments: ${supportingParts.join(" • ")}`
      : "No meaningful supporting segments outside the primary set.";

  const detectedSets: WorkoutDetectedSet[] = structuredSets.map(set => ({
    setId: set.setId,
    classification: set.classification,
    repetitionCount: set.intervals.length,
    averageDurationSeconds: set.averageDurationSeconds,
    averagePower: set.averagePower,
    averagePercentOfFTP: set.averagePercentOfFTP,
    averageRecoverySeconds: set.averageRecoverySeconds,
    powerVariationPercent: set.powerVariationPercent,
    durationVariationPercent: set.durationVariationPercent,
    cadenceVariationPercent: set.cadenceVariationPercent,
    heartRateProgression: set.heartRateProgression,
    confidence: set.confidence,
    isPrimarySet: set.setId === primarySet.setId,
    members: set.intervals.map(interval => interval.id)
  }));

  const baseConfidence = average(primaryIntervals.map(interval => interval.confidence)) ?? primarySet.confidence;
  const confidence = clamp(baseConfidence * 0.65 + primarySet.coherence * 0.35, 0, 1);

  const warnings = [...intervalDetectionResult.warnings];
  if ((intervalConsistencyPercent ?? 100) < 70) {
    warnings.push("Primary set interval power consistency is low.");
  }

  const summary = isMixed
    ? `Primary stimulus is ${displayWorkoutType(primarySet.workoutType)} with additional structured components.`
    : `Primary stimulus identified as ${workoutName}.`;

  return {
    status: "available",
    workoutType,
    workoutName,
    summary,
    confidence,
    classificationCategory: "structured-workout",
    explanation: `Structured classification was driven by ${primaryIntervals.length} repeated ${displayWorkoutType(primarySet.workoutType)} efforts with similar duration, recovery, and power targets.`,
    confidenceFactors: [
      { label: "Power distribution", score: clamp((average(primaryIntervals.map(interval => interval.confidence)) ?? 0.5), 0, 1), reason: `Repeated efforts centered on ${displayWorkoutType(primarySet.workoutType)} intensity.` },
      { label: "Repetition score", score: clamp((primaryIntervals.length - 1) / 4, 0, 1), reason: `${primaryIntervals.length} repeated efforts were detected.` },
      { label: "Variability index", score: analytics.variabilityIndex === null || analytics.variabilityIndex === undefined ? 0.4 : Math.max(0, 1 - Math.abs(analytics.variabilityIndex - 1.12) / 0.25), reason: analytics.variabilityIndex === null || analytics.variabilityIndex === undefined ? "Variability index was unavailable." : `Variability index was ${analytics.variabilityIndex.toFixed(2)}.` },
      { label: "Elapsed duration", score: clamp((analytics.validPowerDurationSeconds ?? 0) / 3600 / 1.25, 0, 1), reason: `${((analytics.validPowerDurationSeconds ?? 0) / 3600).toFixed(1)} hours of valid ride time were available.` }
    ],
    continuousEffortSummary: supportingSegmentSummary,
    primaryStructuredType: primarySet.workoutType,
    secondaryStructuredTypes,
    structuredSetCount: structuredSets.length,
    primaryIntervalClassification: primarySet.classification,
    repetitionCount: primaryIntervals.length,
    plannedEquivalentDurationSeconds: patternMatch.plannedSeconds,
    averageWorkIntervalDurationSeconds: averageDurationSeconds,
    averageRecoveryDurationSeconds: primarySet.averageRecoverySeconds,
    averageWorkPower: primarySet.averagePower,
    averageWorkPercentOfFTP: primarySet.averagePercentOfFTP,
    averagePowerAcrossPrimarySet,
    powerVariationPercent,
    durationVariationPercent,
    intervalConsistencyPercent,
    firstRepAveragePower,
    finalRepAveragePower,
    powerFadePercent,
    powerTrendPercentPerRep,
    powerFadeStatus,
    averageCadenceAcrossPrimarySet: average(primaryCadence),
    cadenceVariationPercent,
    cadenceConsistencyPercent,
    firstRepAverageHeartRate,
    finalRepAverageHeartRate,
    heartRateChangeBpm,
    heartRateChangePercent,
    heartRateProgression,
    completedWorkDurationSeconds: primarySet.totalWorkSeconds,
    supportingSegmentSummary,
    detectedSets,
    warnings
  };
}
